(() => {
  const PREFIX = "[FTR Loop Bundle]";
  const LOOP_STOREFRONT_API =
    "https://api.loopsubscriptions.com/storefront/2023-10/bundle";

  const memoryCache = new Map();

  const state = (window.FTRLoopBundle = window.FTRLoopBundle || {});

  if (state.scriptLoaded) {
    state.initialize?.(document);
    return;
  }

  state.scriptLoaded = true;
  state.bundleId ??= null;
  state.bundleDetails ??= null;
  state.selectedSellingPlanId ??= null;
  state.selectedSellingPlan ??= null;
  state.selectedSellingPlanShopifyId ??= null;
  state.sellingPlans ??= [];
  state.purchaseType ??= "one-time";
  state.readyPromise ??= null;

  console.log(PREFIX, "Script loaded");

  function getLoopBundleIdFromUrl() {
    const params = new URLSearchParams(window.location.search);

    const value = params.get("loop_bundle_id");

    return value ? String(value).trim() : null;
  }

  function getLoopBundleIdFromMetafield(wrapper) {
    return normalizeLoopBundleId(wrapper?.dataset?.loopBundleId);
  }

  function syncLoopBundleIdInUrl(bundleId) {
    if (!bundleId || !window.history?.replaceState) {
      return;
    }

    const url = new URL(window.location.href);

    if (url.searchParams.get("loop_bundle_id") === bundleId) {
      return;
    }

    url.searchParams.set("loop_bundle_id", bundleId);

    window.history.replaceState(
      window.history.state,
      "",
      `${url.pathname}${url.search}${url.hash}`,
    );

    console.log(PREFIX, "Bundle URL updated from product metafield:", url.href);
  }

  function getPublicLoopBundleId() {
    const value =
      state.publicBundleId ||
      state.bundleDetails?.id ||
      state.bundleDetails?.nanoId;

    return value && /^[0-9A-HJKMNP-TV-Z]{26}$/i.test(String(value))
      ? String(value)
      : null;
  }

  function normalizeLoopBundleId(value) {
    if (!value) return null;

    const trimmedValue = String(value).trim();

    const urlMatch = trimmedValue.match(
      /(?:\/bundles\/|\/bundle\/)([a-zA-Z0-9_-]+)(?:[/?#]|$)/,
    );

    if (urlMatch?.[1]) {
      return urlMatch[1];
    }

    return /^[a-zA-Z0-9_-]+$/.test(trimmedValue) ? trimmedValue : null;
  }

  function normalizeShopifyId(value) {
    if (value == null || value === "") {
      return null;
    }

    const parts = String(value).split("/");

    return parts[parts.length - 1];
  }

  function positiveNumber(value) {
    const number = Number(normalizeShopifyId(value));

    return Number.isInteger(number) && number > 0 ? number : null;
  }

  function unwrap(payload) {
    if (!payload || payload.success === false) {
      throw new Error(
        payload?.code ||
          payload?.message ||
          "Loop returned an invalid response.",
      );
    }

    return payload.data ?? payload;
  }

  async function debugFetch(url, options = {}) {
    const response = await fetch(url, {
      ...options,
      headers: {
        Accept: "application/json",
        ...(options?.headers || {}),
      },
    });

    const rawText = await response.text();

    let payload;

    try {
      payload = JSON.parse(rawText);
    } catch (_) {
      payload = rawText;
    }

    console.log(PREFIX, "API response:", {
      url,
      status: response.status,
      statusText: response.statusText,
      payload,
    });

    return {
      response,
      payload,
      rawText,
    };
  }

  function buildLoopBundleDetailsRequest({ bundleId }) {
    return {
      url:
        `${window.location.origin}` +
        `/a/loop_subscriptions/bundle/` +
        `${encodeURIComponent(bundleId)}`,

      options: {
        method: "GET",
        credentials: "same-origin",
        headers: {
          Accept: "text/html",
        },
      },
    };
  }

  function extractHostedBundlePayload(rawHtml, bundleId) {
    const documentNode = new DOMParser().parseFromString(rawHtml, "text/html");

    const nextDataNode = documentNode.querySelector("#__NEXT_DATA__");

    if (!nextDataNode?.textContent) {
      throw new Error(
        `Loop's hosted page did not include bundle data for ${bundleId}.`,
      );
    }

    const nextData = JSON.parse(nextDataNode.textContent);

    const pageProps = nextData?.props?.pageProps;

    if (pageProps?.bundlePageContext?.isError || !pageProps?.bundle) {
      throw new Error(
        pageProps?.bundlePageContext?.error ||
          `Unable to load Loop bundle ${bundleId}.`,
      );
    }

    if (normalizeLoopBundleId(pageProps.bundle.loopBundleId) !== bundleId) {
      throw new Error("Loop returned data for a different bundle ID.");
    }

    return pageProps.bundle;
  }

  async function loopJsonFetch(url, options) {
    const { response, payload } = await debugFetch(url, options);

    if (!response.ok) {
      const error = new Error(
        payload?.message ||
          payload?.code ||
          `Loop request failed (${response.status}).`,
      );

      error.responseStatus = response.status;

      error.responsePayload = payload;

      throw error;
    }

    return unwrap(payload);
  }

  function directBundleId(wrapper) {
    const metafieldBundleId = getLoopBundleIdFromMetafield(wrapper);

    if (metafieldBundleId) {
      syncLoopBundleIdInUrl(metafieldBundleId);

      console.log(
        PREFIX,
        "Bundle ID resolved from product metafield:",
        metafieldBundleId,
      );

      return metafieldBundleId;
    }

    const urlBundleId = normalizeLoopBundleId(getLoopBundleIdFromUrl());

    console.log(PREFIX, "URL bundle ID:", getLoopBundleIdFromUrl());

    if (urlBundleId) {
      console.log(PREFIX, "Bundle ID resolved from URL:", urlBundleId);

      return urlBundleId;
    }

    return null;
  }

  function collectLoopVariantIds(bundleDetails) {
    const variantIds = new Set();

    const products = Array.isArray(bundleDetails?.products)
      ? bundleDetails.products
      : [];

    products.forEach((product) => {
      const variants = Array.isArray(product?.variants) ? product.variants : [];

      variants.forEach((variant) => {
        const variantId = positiveNumber(variant?.shopifyId);

        if (variantId) {
          variantIds.add(variantId);
        }
      });
    });

    return variantIds;
  }

  function getCartItemVariantId(item) {
    return positiveNumber(
      item?.variantId ??
        item?.variant_id ??
        item?.selectedVariantId ??
        item?.shopifyVariantId ??
        item?.shopifyId ??
        item?.id,
    );
  }

  function mergeLoopVariants(items) {
    const quantities = new Map();

    items.forEach((item) => {
      const variantId = getCartItemVariantId(item);

      const quantity = Number(item?.quantity || 0);

      if (!variantId || !Number.isFinite(quantity) || quantity <= 0) {
        return;
      }

      quantities.set(variantId, (quantities.get(variantId) || 0) + quantity);
    });

    return Array.from(quantities, ([shopifyId, quantity]) => ({
      shopifyId,
      quantity,
    }));
  }

  function normalizeLoopTransactionId(value) {
    if (
      value == null ||
      typeof value === "number" ||
      typeof value === "boolean"
    ) {
      return null;
    }

    const normalized = String(value).trim().replace(/^"|"$/g, "");

    return normalized || null;
  }

  function isLikelyLoopTransactionId(value) {
    const normalized = normalizeLoopTransactionId(value);

    if (!normalized) {
      return false;
    }

    const rejectedValues = [
      "success",
      "ok",
      "created",
      "transaction created",
      "error creating transaction",
    ];

    return (
      !rejectedValues.includes(normalized.toLowerCase()) &&
      normalized.length >= 16 &&
      /^[A-Za-z0-9_-]+$/.test(normalized)
    );
  }

  function extractLoopTransactionId(payload, rawText = "") {
    const candidates = [
      payload?.data?.loopBundleGuid,

      payload?.loopBundleGuid,

      payload?.data?.txnId,

      payload?.data?.transactionId,

      payload?.data?.id,

      payload?.data?.transaction?.loopBundleGuid,

      payload?.data?.transaction?.txnId,

      payload?.data?.transaction?.transactionId,

      payload?.data?.transaction?.id,

      payload?.transaction?.loopBundleGuid,

      payload?.transaction?.txnId,

      payload?.transaction?.transactionId,

      payload?.transaction?.id,

      payload?.result?.loopBundleGuid,

      payload?.result?.txnId,

      payload?.result?.transactionId,

      payload?.result?.id,

      payload?.txnId,

      payload?.transactionId,

      payload?.id,
    ];

    if (typeof payload === "string") {
      candidates.push(payload);
    }

    if (typeof payload?.data === "string") {
      candidates.push(payload.data);
    }

    if (typeof payload?.result === "string") {
      candidates.push(payload.result);
    }

    if (rawText) {
      candidates.push(rawText);
    }

    for (const candidate of candidates) {
      const normalized = normalizeLoopTransactionId(candidate);

      if (normalized && isLikelyLoopTransactionId(normalized)) {
        return normalized;
      }
    }

    return null;
  }

  async function readLoopResponse(response) {
    const rawText = await response.text();

    let payload = null;

    if (rawText) {
      try {
        payload = JSON.parse(rawText);
      } catch (_) {
        payload = rawText;
      }
    }

    console.log(PREFIX, "Raw transaction HTTP response:", {
      status: response.status,

      statusText: response.statusText,

      contentType: response.headers?.get?.("content-type") || null,

      rawText,

      payload,

      payloadType: typeof payload,
    });

    return {
      rawText,
      payload,
    };
  }

  async function getBundleDetails(bundleId, domain) {
    const cacheKey = `hosted-bundle-v1:` + `${domain}:` + `${bundleId}`;

    if (memoryCache.has(cacheKey)) {
      return memoryCache.get(cacheKey);
    }

    try {
      const stored = sessionStorage.getItem(`ftr-loop-${cacheKey}`);

      if (stored) {
        const parsed = JSON.parse(stored);

        memoryCache.set(cacheKey, parsed);

        return parsed;
      }
    } catch (_) {}

    const request = buildLoopBundleDetailsRequest({
      bundleId,
      shopDomain: domain,
    });

    console.log(PREFIX, "Request URL:", request.url);

    console.log(PREFIX, "Request options:", request.options);

    const { response, rawText, payload } = await debugFetch(
      request.url,
      request.options,
    );

    console.log(PREFIX, "Response status:", response.status);

    console.log(PREFIX, "Response payload:", payload);

    if (!response.ok) {
      throw new Error(
        `Unable to load Loop bundle ${bundleId} (${response.status}).`,
      );
    }

    const details = extractHostedBundlePayload(rawText, bundleId);

    memoryCache.set(cacheKey, details);

    try {
      sessionStorage.setItem(`ftr-loop-${cacheKey}`, JSON.stringify(details));
    } catch (_) {}

    return details;
  }

  async function getOfficialBundleDetails(publicBundleId, domain) {
    const url =
      `${LOOP_STOREFRONT_API}/` +
      `${encodeURIComponent(publicBundleId)}` +
      `?myshopifyDomain=` +
      `${encodeURIComponent(domain)}`;

    const { response, payload } = await debugFetch(url, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    });

    if (!response.ok || payload?.success === false || !payload?.data) {
      throw new Error(
        payload?.message || "Unable to load official Loop bundle details.",
      );
    }

    return payload.data;
  }

  async function resolveLoopBundleId(wrapper) {
    const domain = wrapper.dataset.shopDomain;

    const configuredBundleId = directBundleId(wrapper);

    if (!configuredBundleId) {
      throw new Error("Loop bundle ID is missing from the product metafield.");
    }

    return {
      bundleId: configuredBundleId,

      details: await getBundleDetails(configuredBundleId, domain),

      source: getLoopBundleIdFromMetafield(wrapper)
        ? "product.metafields.custom.loop_bundle_id"
        : "loop_bundle_id URL parameter",
    };
  }

  function getLoopSellingPlans(bundleDetails) {
    const plans =
      bundleDetails?.sellingPlans ||
      bundleDetails?.selling_plans ||
      bundleDetails?.subscriptionSellingPlans ||
      bundleDetails?.bundle?.sellingPlans ||
      [];

    return Array.isArray(plans) ? plans : [];
  }

  function getShopifySellingPlanId(plan) {
    const value =
      plan?.adminGraphQlApiId ??
      plan?.shopifyId ??
      plan?.sellingPlanShopifyId ??
      plan?.shopifySellingPlanId ??
      plan?.selling_plan_shopify_id ??
      null;

    if (value == null) {
      return null;
    }

    const numericValue = Number(String(value).split("/").pop());

    return Number.isFinite(numericValue) && numericValue > 0
      ? numericValue
      : null;
  }

  function getInternalSellingPlanId(plan) {
    return positiveNumber(
      plan?.id ?? plan?.sellingPlanId ?? plan?.loopSellingPlanId,
    );
  }

  function getSelectedLoopSellingPlanId() {
    const value =
      window.FTRLoopBundle?.selectedSellingPlanShopifyId ??
      window.CustomLoopBundle?.selectedSellingPlanShopifyId ??
      null;

    if (value == null || value === "") {
      return null;
    }

    const numericId = Number(String(value).split("/").pop());

    return Number.isFinite(numericId) && numericId > 0 ? numericId : null;
  }

  function planDescription(plan) {
    return (
      plan?.deliveryDescription ||
      plan?.billingDescription ||
      plan?.description ||
      plan?.intervalDescription ||
      ""
    );
  }

  function getSellingPlanDiscountPercentage(plan) {
    const policy = plan?.pricingPolicies?.[0];

    const possibleValues = [
      policy?.adjustmentType === "PERCENTAGE"
        ? policy?.adjustmentValue?.percentage
        : null,

      plan?.discountPercentage,

      plan?.discount?.percentage,

      plan?.discountValue,

      plan?.percentage,

      plan?.priceAdjustment?.value,

      plan?.priceAdjustments?.[0]?.value,

      plan?.sellingPlanPriceAdjustments?.[0]?.value,

      plan?.discounts?.[0]?.type === "PERCENTAGE"
        ? plan?.discounts?.[0]?.value
        : null,
    ];

    for (const value of possibleValues) {
      const number = Number(value);

      if (Number.isFinite(number) && number > 0) {
        return number;
      }
    }

    return 0;
  }

  function calculateLoopBundlePrice({
    basePriceInCents,
    purchaseType,
    selectedPlan,
  }) {
    if (purchaseType !== "subscription" || !selectedPlan) {
      return basePriceInCents;
    }

    const percentage = getSellingPlanDiscountPercentage(selectedPlan);

    return Math.round(basePriceInCents * (1 - percentage / 100));
  }

  function formatMoney(cents, wrapper) {
    const currency =
      state.bundleDetails?.currencyCode ||
      document.documentElement.dataset.currency ||
      "USD";

    try {
      return new Intl.NumberFormat(document.documentElement.lang || "en", {
        style: "currency",
        currency,
      }).format(cents / 100);
    } catch (_) {
      return String(cents / 100);
    }
  }

  function getPriceElements(wrapper) {
    const product = wrapper.closest(".product");

    const container = wrapper.closest(".container");

    return Array.from(
      new Set([
        ...(product?.querySelectorAll(".product-info__price .price__current") ||
          []),

        ...(container?.querySelectorAll(".card--sticky-atc .price__current") ||
          []),

        ...document.querySelectorAll(".card--sticky-atc .price__current"),

        ...(product?.querySelectorAll(
          [
            "[data-loop-bundle-price]",
            "[data-bundle-total]",
            "[data-selected-total]",
          ].join(", "),
        ) || []),
      ]),
    );
  }

  function updatePurchaseOptionsPriceUI(wrapper) {
    if (!wrapper) return;

    const oneTimePriceInCents = positiveNumber(wrapper.dataset.oneTimePrice);

    if (!oneTimePriceInCents) {
      return;
    }

    /*
     * Even while One-time is selected,
     * preview the first available
     * subscription plan.
     */
    const previewPlan =
      state.selectedSellingPlan || state.sellingPlans?.[0] || null;

    const discountPercentage = previewPlan
      ? getSellingPlanDiscountPercentage(previewPlan)
      : 0;

    const subscriptionPriceInCents =
      previewPlan && discountPercentage > 0
        ? Math.round(oneTimePriceInCents * (1 - discountPercentage / 100))
        : oneTimePriceInCents;

    const oneTimePriceElement = wrapper.querySelector(
      "[data-loop-one-time-price]",
    );

    const subscriptionComparePriceElement = wrapper.querySelector(
      "[data-loop-subscription-compare-price]",
    );

    const subscriptionPriceElement = wrapper.querySelector(
      "[data-loop-subscription-price]",
    );

    const saveBadge = wrapper.querySelector("[data-loop-save-badge]");

    if (oneTimePriceElement) {
      oneTimePriceElement.textContent = formatMoney(
        oneTimePriceInCents,
        wrapper,
      );
    }

    if (subscriptionComparePriceElement) {
      subscriptionComparePriceElement.textContent = formatMoney(
        oneTimePriceInCents,
        wrapper,
      );

      subscriptionComparePriceElement.hidden = !(discountPercentage > 0);
    }

    if (subscriptionPriceElement) {
      subscriptionPriceElement.textContent = formatMoney(
        subscriptionPriceInCents,
        wrapper,
      );
    }

    if (saveBadge) {
      if (discountPercentage > 0) {
        saveBadge.hidden = false;

        saveBadge.textContent = `Save ${discountPercentage}%`;
      } else {
        saveBadge.hidden = true;

        saveBadge.textContent = "";
      }
    }

    console.log(PREFIX, "Purchase option price UI:", {
      oneTimePriceInCents,
      subscriptionPriceInCents,
      discountPercentage,
      previewPlan,
    });
  }

  function refreshLoopBundlePrice(
    wrapper = document.querySelector("[data-loop-custom-bundle]"),
  ) {
    if (!wrapper) return;

    const oneTimePriceInCents = positiveNumber(wrapper.dataset.oneTimePrice);

    if (!oneTimePriceInCents) {
      return;
    }

    const currentPriceInCents = calculateLoopBundlePrice({
      basePriceInCents: oneTimePriceInCents,

      purchaseType: state.purchaseType,

      selectedPlan: state.selectedSellingPlan,
    });

    getPriceElements(wrapper).forEach((element) => {
      element.dataset.oneTimePrice = String(oneTimePriceInCents);

      element.dataset.currentPrice = String(currentPriceInCents);

      element.textContent = formatMoney(currentPriceInCents, wrapper);
    });

    updatePurchaseOptionsPriceUI(wrapper);

    console.log(PREFIX, "Main bundle price updated:", {
      purchaseType: state.purchaseType,

      oneTimePriceInCents,

      currentPriceInCents,
    });
  }

  function setPurchaseSelection({ wrapper, purchaseType, plan }) {
    console.log(PREFIX, "Raw selected plan:", plan);

    state.purchaseType = purchaseType;

    state.selectedSellingPlan = purchaseType === "subscription" ? plan : null;

    state.selectedSellingPlanId = state.selectedSellingPlan
      ? getInternalSellingPlanId(state.selectedSellingPlan)
      : null;

    state.selectedSellingPlanShopifyId = state.selectedSellingPlan
      ? getShopifySellingPlanId(state.selectedSellingPlan)
      : null;

    wrapper.querySelectorAll(".loop-purchase-option").forEach((card) => {
      card.classList.toggle(
        "is-selected",
        card.dataset.purchaseType === purchaseType,
      );
    });

    const frequency = wrapper.querySelector(".loop-purchase-option__frequency");

    const select = frequency?.querySelector("select");

    if (frequency) {
      frequency.hidden = purchaseType !== "subscription";
    }

    if (select) {
      select.disabled = purchaseType !== "subscription";
    }

    console.log(PREFIX, "Purchase type:", state.purchaseType);

    console.log(PREFIX, "Selected selling plan:", state.selectedSellingPlan);

    console.log(PREFIX, "Loop selling plan ID:", state.selectedSellingPlanId);

    console.log(
      PREFIX,
      "Shopify selling plan ID:",
      state.selectedSellingPlanShopifyId,
    );

    refreshLoopBundlePrice(wrapper);

    document.dispatchEvent(
      new CustomEvent("ftr:loop:selling-plan-change", {
        detail: {
          purchaseType: state.purchaseType,

          sellingPlanId: state.selectedSellingPlanShopifyId,

          sellingPlan: state.selectedSellingPlan,
        },
      }),
    );
  }

  function syncSelectedPlanFromWidget(scope = document) {
    const wrapper = scope?.matches?.("[data-loop-custom-bundle]")
      ? scope
      : scope?.querySelector?.("[data-loop-custom-bundle]") ||
        document.querySelector("[data-loop-custom-bundle]");

    if (!wrapper) {
      return getSelectedLoopSellingPlanId();
    }

    const checkedPurchaseType = wrapper.querySelector(
      '.loop-purchase-options input[type="radio"]:checked',
    )?.value;

    if (checkedPurchaseType !== "subscription") {
      setPurchaseSelection({
        wrapper,
        purchaseType: "one-time",
        plan: null,
      });

      return null;
    }

    const select = wrapper.querySelector(
      ".loop-purchase-option__frequency select",
    );

    const selectedOption = select?.selectedOptions?.[0];

    let selectedPlan = state.sellingPlans?.[Number(select?.value)];

    if (!selectedPlan && selectedOption) {
      const shopifyId = positiveNumber(
        selectedOption.dataset.shopifySellingPlanId,
      );

      const loopId = positiveNumber(selectedOption.dataset.loopSellingPlanId);

      selectedPlan = state.sellingPlans?.find(
        (plan) =>
          getShopifySellingPlanId(plan) === shopifyId ||
          positiveNumber(plan?.sellingPlanId) === loopId,
      );
    }

    console.log(PREFIX, "Raw selected plan:", selectedPlan);

    if (!selectedPlan) {
      state.purchaseType = "subscription";

      state.selectedSellingPlan = null;

      state.selectedSellingPlanId = null;

      state.selectedSellingPlanShopifyId = null;

      return null;
    }

    setPurchaseSelection({
      wrapper,
      purchaseType: "subscription",
      plan: selectedPlan,
    });

    return getSelectedLoopSellingPlanId();
  }

  function renderOptions(wrapper, details) {
    const host = wrapper.querySelector("[data-loop-purchase-options]");

    if (!host || host.dataset.rendered === "true") {
      return;
    }

    const plans = getLoopSellingPlans(details).filter((plan) =>
      getShopifySellingPlanId(plan),
    );

    state.sellingPlans = plans;

    state.purchaseType = "one-time";

    state.selectedSellingPlanId = null;

    state.selectedSellingPlanShopifyId = null;

    state.selectedSellingPlan = null;

    const fieldset = document.createElement("fieldset");

    fieldset.className = "loop-purchase-options";

    fieldset.innerHTML = `
      <legend class="visually-hidden">
        Purchase Options
      </legend>

      <div class="loop-purchase-options__heading">
        Purchase Options
      </div>

      <label
        class="loop-purchase-option loop-purchase-option--one-time is-selected"
        data-purchase-type="one-time"
      >
        <span class="loop-purchase-option__left">
          <input
            class="visually-hidden"
            type="radio"
            name="${wrapper.id}-purchase-type"
            value="one-time"
            checked
          >

          <span
            class="loop-purchase-option__radio"
            aria-hidden="true"
          ></span>

          <span
            class="loop-purchase-option__title"
            data-loop-title="one-time"
          >
            One-time purchase
          </span>
        </span>

        <span class="loop-purchase-option__price">
          <span
            class="loop-purchase-option__current-price"
            data-loop-one-time-price
          ></span>
        </span>
      </label>

      <div
        class="loop-purchase-option loop-purchase-option--subscription"
        data-purchase-type="subscription"
      >
        <label class="loop-purchase-option__header">
          <span class="loop-purchase-option__left">
            <input
              class="visually-hidden"
              type="radio"
              name="${wrapper.id}-purchase-type"
              value="subscription"
            >

            <span
              class="loop-purchase-option__radio"
              aria-hidden="true"
            ></span>

            <span
              class="loop-purchase-option__title"
              data-loop-title="subscription"
            >
              Subscribe and Save
            </span>
          </span>

          <span class="loop-purchase-option__subscription-meta">
            <span
              class="loop-purchase-option__save-badge"
              data-loop-save-badge
              hidden
            ></span>

            <span class="loop-purchase-option__price-stack">
              <span
                class="loop-purchase-option__compare-price"
                data-loop-subscription-compare-price
              ></span>

              <span
                class="loop-purchase-option__current-price loop-purchase-option__subscription-price"
                data-loop-subscription-price
              ></span>
            </span>
          </span>
        </label>

        <div
          class="loop-purchase-option__frequency"
          hidden
        >
          <label
            for="${wrapper.id}-selling-plan"
          >
            Frequency
          </label>

          <select
            id="${wrapper.id}-selling-plan"
            disabled
          ></select>
        </div>
      </div>

      <div class="loop-subscription-details">
        <span
          class="loop-subscription-details__icon"
          aria-hidden="true"
        >
          ↻
        </span>

        <span>
          Subscription details
        </span>
      </div>
    `;

    const subscriptionCard = fieldset.querySelector(
      ".loop-purchase-option--subscription",
    );

    const select = fieldset.querySelector(
      ".loop-purchase-option__frequency select",
    );

    const detailsRow = fieldset.querySelector(".loop-subscription-details");

    if (!plans.length) {
      if (subscriptionCard) {
        subscriptionCard.hidden = true;
      }

      if (detailsRow) {
        detailsRow.hidden = true;
      }
    }

    plans.forEach((plan, index) => {
      const option = document.createElement("option");

      option.value = String(index);

      option.textContent =
        plan?.name || planDescription(plan) || `Subscription ${index + 1}`;

      option.dataset.shopifySellingPlanId = String(
        getShopifySellingPlanId(plan),
      );

      option.dataset.loopSellingPlanId = String(
        getInternalSellingPlanId(plan) || "",
      );

      select.append(option);
    });

    const oneTimeRadio = fieldset.querySelector('input[value="one-time"]');

    const subscriptionRadio = fieldset.querySelector(
      'input[value="subscription"]',
    );

    oneTimeRadio?.addEventListener("change", () => {
      setPurchaseSelection({
        wrapper,
        purchaseType: "one-time",
        plan: null,
      });
    });

    subscriptionRadio?.addEventListener("change", () => {
      const selectedPlan = plans[Number(select.value) || 0];

      setPurchaseSelection({
        wrapper,
        purchaseType: "subscription",
        plan: selectedPlan,
      });
    });

    select?.addEventListener("change", () => {
      subscriptionRadio.checked = true;

      const selectedPlan = plans[Number(select.value)];

      setPurchaseSelection({
        wrapper,
        purchaseType: "subscription",
        plan: selectedPlan,
      });
    });

    subscriptionCard?.addEventListener("click", (event) => {
      if (event.target.closest("select")) {
        return;
      }

      if (state.purchaseType === "subscription") {
        return;
      }

      subscriptionRadio.checked = true;

      const selectedPlan = plans[Number(select.value) || 0];

      setPurchaseSelection({
        wrapper,
        purchaseType: "subscription",
        plan: selectedPlan,
      });
    });

    host.replaceChildren(fieldset);

    host.dataset.rendered = "true";

    refreshLoopBundlePrice(wrapper);
  }

  function showWidgetError(wrapper, message) {
    const host = wrapper?.querySelector("[data-loop-purchase-options]");

    if (host) {
      host.replaceChildren();
    }

    const error = wrapper?.querySelector("[data-loop-error]");

    if (error) {
      error.textContent = message;

      error.hidden = false;
    }
  }

  function getPublicBoxSizes(bundleDetails) {
    if (Array.isArray(bundleDetails?.boxSizes)) {
      return bundleDetails.boxSizes;
    }

    return (
      Array.isArray(bundleDetails?.variants) ? bundleDetails.variants : []
    )
      .map((variant) => ({
        ...variant,

        id: variant.nanoId || null,

        minimum: variant.minValue,

        maximum: variant.maxValue,
      }))
      .filter((boxSize) => boxSize.id);
  }

  function getSelectedLoopBoxSizeId({
    bundleDetails,
    selectedItemCount,
    existingSelectedBoxSizeId,
  }) {
    const boxSizes = getPublicBoxSizes(bundleDetails);

    console.log(PREFIX, "Available box sizes:", boxSizes);

    if (existingSelectedBoxSizeId) {
      const exact = boxSizes.find(
        (boxSize) => String(boxSize.id) === String(existingSelectedBoxSizeId),
      );

      if (exact) {
        return exact.id;
      }
    }

    const matches = boxSizes.filter((boxSize) => {
      const exact = Number(
        boxSize.quantity ?? boxSize.size ?? boxSize.itemCount,
      );

      if (Number.isFinite(exact) && exact > 0) {
        return exact === selectedItemCount;
      }

      const minimum = Number(
        boxSize.minimum ??
          boxSize.min ??
          boxSize.minQuantity ??
          boxSize.minSize,
      );

      const maximum = Number(
        boxSize.maximum ??
          boxSize.max ??
          boxSize.maxQuantity ??
          boxSize.maxSize,
      );

      return (
        (!Number.isFinite(minimum) ||
          minimum <= 0 ||
          selectedItemCount >= minimum) &&
        (!Number.isFinite(maximum) ||
          maximum <= 0 ||
          selectedItemCount <= maximum)
      );
    });

    return matches.length === 1 ? matches[0].id : null;
  }

  async function initializeWrapper(wrapper) {
    if (!wrapper || wrapper.dataset.loopInitialized === "true") {
      return;
    }

    wrapper.dataset.loopInitialized = "true";

    try {
      const resolved = await resolveLoopBundleId(wrapper);

      state.bundleId = resolved.bundleId;

      state.hostedBundleDetails = resolved.details;

      state.publicBundleId = resolved.details?.nanoId || null;

      if (!state.publicBundleId) {
        throw new Error("Loop's public Storefront bundle ID is missing.");
      }

      state.bundleDetails = await getOfficialBundleDetails(
        state.publicBundleId,
        wrapper.dataset.shopDomain,
      );

      state.resolutionSource = resolved.source;

      const errorElement = wrapper.querySelector("[data-loop-error]");

      if (errorElement) {
        errorElement.hidden = true;

        errorElement.textContent = "";
      }

      const sellingPlans = getLoopSellingPlans(resolved.details);

      console.log(PREFIX, "Resolved bundle ID:", resolved.bundleId);

      console.log(PREFIX, "Bundle details:", resolved.details);

      console.log(PREFIX, "Selling plans:", sellingPlans);

      renderOptions(wrapper, resolved.details);

      console.log(PREFIX, {
        productId: wrapper.dataset.productId,

        resolvedBundleId: state.bundleId,

        source: resolved.source,
      });
    } catch (error) {
      console.error(PREFIX, "Initialization failed.", error);

      const missingId = !directBundleId(wrapper);

      showWidgetError(
        wrapper,
        missingId
          ? "Loop bundle ID is missing from this product."
          : "Subscription options could not be loaded.",
      );

      wrapper.dataset.loopInitialized = "false";
    }
  }

  function initialize(root = document) {
    const wrappers = [];

    if (root.matches?.("[data-loop-custom-bundle]")) {
      wrappers.push(root);
    }

    root
      .querySelectorAll?.("[data-loop-custom-bundle]")
      .forEach((wrapper) => wrappers.push(wrapper));

    if (
      !wrappers.length ||
      !(
        new URLSearchParams(location.search).get("view") === "bundle" ||
        wrappers.some((wrapper) => wrapper.dataset.templateSuffix === "bundle")
      )
    ) {
      return Promise.resolve();
    }

    state.readyPromise = Promise.all(wrappers.map(initializeWrapper));

    return state.readyPromise;
  }

  function findHostedBundleVariant(selectedBoxSizeId, selectedItemCount) {
    const variants = Array.isArray(state.hostedBundleDetails?.variants)
      ? state.hostedBundleDetails.variants
      : [];

    const exact = variants.find((variant) =>
      [variant?.nanoId, variant?.id, variant?.bundleVariantId].some(
        (value) => value != null && String(value) === String(selectedBoxSizeId),
      ),
    );

    if (exact) {
      return exact;
    }

    return (
      variants.find((variant) => {
        const minimum = Number(
          variant?.minValue ?? variant?.minimum ?? variant?.minQuantity,
        );

        const maximum = Number(
          variant?.maxValue ?? variant?.maximum ?? variant?.maxQuantity,
        );

        return (
          (!Number.isFinite(minimum) || selectedItemCount >= minimum) &&
          (!Number.isFinite(maximum) || selectedItemCount <= maximum)
        );
      }) || null
    );
  }

  function getInternalLoopTransactionData({
    selectedBoxSizeId,
    selectedDiscountId,
    selectedItemCount,
  }) {
    const details = state.hostedBundleDetails || {};

    const selectedBundleVariant = findHostedBundleVariant(
      selectedBoxSizeId,
      selectedItemCount,
    );

    const discounts = [
      ...(Array.isArray(selectedBundleVariant?.discounts)
        ? selectedBundleVariant.discounts
        : []),

      ...(Array.isArray(details?.discounts) ? details.discounts : []),

      ...(Array.isArray(details?.bundleDiscounts)
        ? details.bundleDiscounts
        : []),
    ];

    const selectedDiscount =
      discounts.find((discount) =>
        [discount?.nanoId, discount?.id, discount?.bundleDiscountId].some(
          (value) =>
            selectedDiscountId != null &&
            String(value) === String(selectedDiscountId),
        ),
      ) ||
      selectedBundleVariant?.discount ||
      details?.bundleDiscount ||
      discounts[0] ||
      null;

    state.selectedBundleVariant = selectedBundleVariant;

    state.selectedBundleDiscount = selectedDiscount;

    return {
      bundleId: positiveNumber(
        details?.bundleId ?? details?.id ?? state.internalBundleId,
      ),

      bundleVariantId: positiveNumber(
        selectedBundleVariant?.bundleVariantId ??
          selectedBundleVariant?.id ??
          details?.bundleVariantId ??
          state.bundleVariantId,
      ),

      bundleDiscountId: positiveNumber(
        selectedDiscount?.bundleDiscountId ??
          selectedDiscount?.id ??
          selectedBundleVariant?.bundleDiscountId ??
          selectedBundleVariant?.discountId ??
          details?.bundleDiscountId ??
          state.bundleDiscountId,
      ),

      sellingPlanId:
        state.purchaseType === "subscription"
          ? positiveNumber(
              state.selectedSellingPlan?.sellingPlanId ??
                state.selectedSellingPlan?.id ??
                state.selectedSellingPlanId,
            )
          : null,
    };
  }

  async function getLoopBundleCartDetails(transactionId) {
    const normalizedId = normalizeLoopTransactionId(transactionId);

    if (!normalizedId) {
      throw new Error("Missing Loop transaction ID.");
    }

    const shortLocale =
      String(document.documentElement.lang || "en").split("-")[0] || "en";

    const url =
      `https://api-service.loopwork.co/` +
      `bundleTransaction/` +
      `getBundleCartDetails/` +
      `${encodeURIComponent(normalizedId)}` +
      `?locale=` +
      `${encodeURIComponent(shortLocale)}`;

    console.log(PREFIX, "Cart details request:", url);

    const { response, payload, rawText } = await debugFetch(url, {
      method: "GET",
      headers: {
        Accept: "*/*",
      },
    });

    console.log(PREFIX, "Cart details response:", {
      status: response.status,

      rawText,

      payload,
    });

    if (!response.ok) {
      throw new Error(
        payload?.message || "Unable to finalize Loop bundle details.",
      );
    }

    return payload;
  }

  function extractBundleName(cartDetails) {
    const details = cartDetails?.data ?? cartDetails?.result ?? cartDetails;

    const officialDetails =
      state.bundleDetails?.data || state.bundleDetails || {};

    const hostedDetails = state.hostedBundleDetails || {};

    return (
      details?.bundleName ||
      details?.bundle?.bundleName ||
      details?.bundle?.name ||
      details?.bundle?.title ||
      officialDetails?.bundleName ||
      officialDetails?.name ||
      officialDetails?.title ||
      hostedDetails?.bundleName ||
      hostedDetails?.name ||
      hostedDetails?.title ||
      "Bundle"
    );
  }

  async function createLoopHostedTransaction({
    selectedItems,
    selectedBoxSizeId = null,
    selectedDiscountId = null,
  }) {
    const bundleDetails = state.bundleDetails?.data || state.bundleDetails;

    if (!bundleDetails) {
      throw new Error("Loop bundle details have not loaded.");
    }

    const purchaseType = state.purchaseType || "one-time";

    const sellingPlanShopifyId =
      purchaseType === "subscription" ? getSelectedLoopSellingPlanId() : null;

    if (purchaseType === "subscription" && !sellingPlanShopifyId) {
      const error = new Error("Please select a valid subscription frequency.");

      error.customerMessage = error.message;

      throw error;
    }

    const allowedVariantIds = collectLoopVariantIds(bundleDetails);

    console.log(PREFIX, "Stage 1 selected children:", selectedItems);

    const validLoopItems = selectedItems.filter(
      (item) =>
        allowedVariantIds.has(getCartItemVariantId(item)) &&
        Number(item.quantity) > 0,
    );

    const excludedItems = selectedItems.filter(
      (item) => !validLoopItems.includes(item),
    );

    console.log(PREFIX, "Configured Loop children:", validLoopItems);

    const productVariants = mergeLoopVariants(validLoopItems);

    console.log(PREFIX, "Stage 2 productVariants:", productVariants);

    if (!productVariants.length) {
      const error = new Error("No valid Loop bundle products were selected.");

      error.customerMessage =
        "One or more selected bundle products are unavailable.";

      throw error;
    }

    const selectedItemCount = productVariants.reduce(
      (total, item) => total + item.quantity,
      0,
    );

    const boxSizeId = getSelectedLoopBoxSizeId({
      bundleDetails,
      selectedItemCount,
      existingSelectedBoxSizeId: selectedBoxSizeId,
    });

    console.log(PREFIX, "Current box size:", {
      selectedBoxSizeId: boxSizeId,

      boxSizes: getPublicBoxSizes(bundleDetails),

      selectedQuantity: selectedItemCount,
    });

    if (!boxSizeId) {
      const error = new Error(
        "The selected Loop bundle size could not be determined.",
      );

      error.customerMessage =
        "Please select the required number of bundle products.";

      throw error;
    }

    const internalData = getInternalLoopTransactionData({
      selectedBoxSizeId: boxSizeId,

      selectedDiscountId,

      selectedItemCount,
    });

    console.log(PREFIX, "2. Internal IDs:", internalData);

    if (!internalData.bundleId) {
      throw new Error("The Loop internal bundle ID is missing.");
    }

    if (!internalData.bundleVariantId) {
      throw new Error("The Loop bundle variant ID is missing.");
    }

    if (purchaseType === "subscription" && !internalData.sellingPlanId) {
      throw new Error("The Loop selling-plan ID is missing.");
    }

    const loopTransactionPayload = {
      bundleId: internalData.bundleId,

      bundleVariantId: internalData.bundleVariantId,

      bundleDiscountId: internalData.bundleDiscountId,

      sellingPlanId: internalData.sellingPlanId,
    };

    const transactionUrl = "https://bundle.loopwork.co/api/transactions/create";

    console.log(
      PREFIX,
      "Internal transaction request:",
      loopTransactionPayload,
    );

    const response = await fetch(transactionUrl, {
      method: "POST",

      headers: {
        Accept: "*/*",
        "Content-Type": "application/json",
      },

      body: JSON.stringify(loopTransactionPayload),
    });

    const { rawText, payload: transactionResponse } =
      await readLoopResponse(response);

    console.log(PREFIX, "Internal transaction response:", {
      status: response.status,

      rawText,

      payload: transactionResponse,
    });

    console.log(PREFIX, "3. Transaction:", transactionResponse);

    if (!response.ok) {
      console.error(PREFIX, "Storefront transaction error:", {
        transactionUrl,

        loopTransactionPayload,

        responseStatus: response.status,

        responseCode: transactionResponse?.code,

        responseMessage: transactionResponse?.message,

        responsePayload: transactionResponse,
      });

      const error = new Error(
        transactionResponse?.message ||
          "Unable to create the Loop bundle transaction.",
      );

      error.customerMessage =
        transactionResponse?.code === "UNPROCESSABLE_ENTITY"
          ? "One or more selected bundle products are unavailable."
          : "The bundle could not be added. Please try again.";

      throw error;
    }

    if (response.status === 204 || !rawText) {
      console.error(
        PREFIX,
        "Successful transaction response contained no data:",
        {
          status: response.status,

          headers: Object.fromEntries(response.headers?.entries?.() || []),
        },
      );

      throw new Error(
        "Loop returned a successful response without transaction data.",
      );
    }

    const transactionId = extractLoopTransactionId(
      transactionResponse,
      rawText,
    );

    if (!transactionId) {
      console.error(PREFIX, "Could not identify transaction ID:", {
        payload: transactionResponse,

        rawText,

        topLevelKeys:
          transactionResponse && typeof transactionResponse === "object"
            ? Object.keys(transactionResponse)
            : [],

        dataKeys:
          transactionResponse?.data &&
          typeof transactionResponse.data === "object"
            ? Object.keys(transactionResponse.data)
            : [],

        resultKeys:
          transactionResponse?.result &&
          typeof transactionResponse.result === "object"
            ? Object.keys(transactionResponse.result)
            : [],
      });

      throw new Error(
        "Loop transaction succeeded but did not return a recognizable transaction ID.",
      );
    }

    console.log(PREFIX, "Extracted transaction ID:", transactionId);

    const cartDetails = await getLoopBundleCartDetails(transactionId);

    console.log(PREFIX, "4. Cart details:", cartDetails);

    const bundleName = extractBundleName(cartDetails);

    console.log(PREFIX, "5. Bundle name:", bundleName);

    return {
      transactionId,

      txnId: transactionId,

      loopBundleGuid: transactionId,

      payload: transactionResponse,

      rawText,

      transactionPayload: loopTransactionPayload,

      loopTransactionPayload,

      productVariants,

      sellingPlanShopifyId,

      allowedVariantIds,

      excludedItems,

      transactionResponse,

      cartDetails,

      bundleName,
    };
  }

  function isParentBundleLine(item) {
    const parentFlag = item?.properties?._parent;

    return (
      item?.isBundleParent === true ||
      item?.isParent === true ||
      parentFlag === true ||
      parentFlag === "true" ||
      parentFlag === 1 ||
      parentFlag === "1"
    );
  }

  function isBundleCartLine(item) {
    const properties = item?.properties || {};

    if (properties._free_gift === true || properties._free_gift === "true") {
      return false;
    }

    return (
      isParentBundleLine(item) ||
      item?.isBundleChild === true ||
      item?.isChild === true ||
      Boolean(properties._bundleId) ||
      Boolean(properties._parent_product_id) ||
      Boolean(properties._bundle_group)
    );
  }

  function getLoadedLoopBundleName(bundleDetails) {
    const details = bundleDetails?.data || bundleDetails;

    return (
      details?.bundleName ||
      details?.bundle?.bundleName ||
      details?.bundle?.name ||
      details?.bundle?.title ||
      details?.title ||
      details?.name ||
      "Bundle"
    );
  }

  function getLoopBundleName(bundleDetails) {
    const loadedName = getLoadedLoopBundleName(bundleDetails);

    if (loadedName !== "Bundle") {
      return loadedName;
    }

    return (
      state.hostedBundleDetails?.bundleName ||
      state.hostedBundleDetails?.bundle?.bundleName ||
      state.hostedBundleDetails?.bundle?.name ||
      state.hostedBundleDetails?.title ||
      state.hostedBundleDetails?.name ||
      "Bundle"
    );
  }

  function withoutLegacyBundleProperties(properties = {}) {
    const cleanedProperties = {
      ...properties,
    };

    delete cleanedProperties._parent;

    delete cleanedProperties._parent_product_id;

    delete cleanedProperties._bundle_group;

    delete cleanedProperties._set;

    delete cleanedProperties.content;

    delete cleanedProperties._bundleId;

    delete cleanedProperties._bundleName;

    return cleanedProperties;
  }

  function isLoopTransactionGuid(value) {
    if (value == null) {
      return false;
    }

    return /^[0-9A-HJKMNP-TV-Z]{26}$/i.test(String(value).trim());
  }

  function removeLoopPropertiesFromOneTimeItem(item) {
    const cartItem = {
      ...item,

      properties: {
        ...(item?.properties || {}),
      },
    };

    delete cartItem.selling_plan;

    delete cartItem.properties._bundleName;

    if (isLoopTransactionGuid(cartItem.properties._bundleId)) {
      delete cartItem.properties._bundleId;
    }

    return cartItem;
  }

  function buildLoopSubscriptionCartItems({
    selectedChildItems,
    sellingPlanShopifyId,
    transactionId,
    bundleName,
    purchaseType,
    allowedVariantIds,
  }) {
    if (purchaseType === "subscription" && !sellingPlanShopifyId) {
      throw new Error(
        "A valid Shopify selling plan is required for subscription.",
      );
    }

    if (!transactionId) {
      throw new Error("A valid Loop transaction ID is required.");
    }

    const loopQuantities = new Map();

    const separateItems = [];

    selectedChildItems
      .filter((item) => !isParentBundleLine(item))
      .forEach((item) => {
        const variantId = getCartItemVariantId(item);

        const quantity = Number(item.quantity || 1);

        if (!variantId || !Number.isFinite(quantity) || quantity <= 0) {
          console.warn(PREFIX, "Invalid selected item excluded:", item);

          return;
        }

        const isLoopBundleItem = allowedVariantIds.has(variantId);

        if (!isLoopBundleItem) {
          const properties = withoutLegacyBundleProperties(item.properties);

          const separateCartItem = {
            ...item,

            id: variantId,

            quantity,

            ...(Object.keys(properties).length
              ? {
                  properties,
                }
              : {}),
          };

          if (!Object.keys(properties).length) {
            delete separateCartItem.properties;
          }

          delete separateCartItem.selling_plan;

          separateItems.push(separateCartItem);

          return;
        }

        loopQuantities.set(
          variantId,
          (loopQuantities.get(variantId) || 0) + quantity,
        );
      });

    const loopCartItems = Array.from(
      loopQuantities,
      ([variantId, quantity]) => {
        const cartItem = {
          id: variantId,

          quantity,

          properties: {
            _bundleName: bundleName,

            _bundleId: transactionId,
          },
        };

        if (purchaseType === "subscription") {
          cartItem.selling_plan = String(sellingPlanShopifyId);
        }

        return cartItem;
      },
    );

    return [...loopCartItems, ...separateItems];
  }

  function prepareFinalShopifyCartItems({
    items,
    txnId,
    sellingPlanShopifyId,
    purchaseType,
    allowedVariantIds,
    bundleName,
  }) {
    const selectedChildItems = items.filter(
      (item) => !isParentBundleLine(item) && Number(item.quantity) > 0,
    );

    return buildLoopSubscriptionCartItems({
      selectedChildItems,

      sellingPlanShopifyId,

      transactionId: txnId,

      bundleName,

      purchaseType,

      allowedVariantIds,
    });
  }

  state.prepareCartItems = async function prepareCartItems(items, productInfo) {
    if (!state.readyPromise) {
      initialize();
    }

    await state.readyPromise;

    syncSelectedPlanFromWidget(productInfo || document);

    /*
     * ONE-TIME
     *
     * Keep legacy Cart Transform
     * payload and remove only
     * Loop subscription metadata.
     */
    if (state.purchaseType !== "subscription") {
      const oneTimeItems = items.map(removeLoopPropertiesFromOneTimeItem);

      console.log(PREFIX, "One-time legacy Cart Transform payload:", {
        items: oneTimeItems,
      });

      return oneTimeItems;
    }

    /*
     * SUBSCRIPTION
     */
    const selectedItems = items.filter(
      (item) => !isParentBundleLine(item) && Number(item.quantity) > 0,
    );

    console.log(PREFIX, "UI selected items:", selectedItems);

    const selectedBoxSizeId =
      productInfo?.querySelector?.(
        [
          "[data-loop-box-size-id][aria-selected='true']",
          "[data-loop-box-size-id].selected",
        ].join(", "),
      )?.dataset.loopBoxSizeId ||
      state.selectedBoxSizeId ||
      null;

    const selectedDiscountId =
      productInfo?.querySelector?.(
        [
          "[data-loop-discount-id][aria-selected='true']",
          "[data-loop-discount-id].selected",
        ].join(", "),
      )?.dataset.loopDiscountId ||
      state.selectedDiscountId ||
      null;

    console.log(PREFIX, "Raw selected items:", selectedItems);

    console.log(PREFIX, "1. Selected children:", selectedItems);

    const transaction = await createLoopHostedTransaction({
      selectedItems,

      selectedBoxSizeId,

      selectedDiscountId,
    });

    const transactionId =
      transaction.transactionId ||
      transaction.loopBundleGuid ||
      transaction.txnId;

    if (!transactionId) {
      throw new Error("Missing Loop transaction ID.");
    }

    console.log(PREFIX, "Shopify cart items before filtering:", items);

    const finalCartItems = prepareFinalShopifyCartItems({
      items,

      txnId: transactionId,

      sellingPlanShopifyId: transaction.sellingPlanShopifyId,

      purchaseType: state.purchaseType,

      allowedVariantIds: transaction.allowedVariantIds,

      bundleName: transaction.bundleName,
    });

    console.log(PREFIX, "6. Final cart items:", finalCartItems);

    if (!Array.isArray(finalCartItems) || finalCartItems.length === 0) {
      console.error(PREFIX, "Empty cart items:", {
        selectedBundleChildItems: selectedItems,

        txnId: transactionId,

        bundleName: transaction.bundleName,

        purchaseType: state.purchaseType,

        sellingPlanShopifyId: transaction.sellingPlanShopifyId,
      });

      throw new Error("The bundle cart payload is empty.");
    }

    const totalSelectedQuantity = selectedItems.reduce(
      (sum, item) => sum + Number(item.quantity || 0),
      0,
    );

    const totalCartQuantity = finalCartItems.reduce(
      (sum, item) => sum + Number(item.quantity || 0),
      0,
    );

    if (totalSelectedQuantity !== totalCartQuantity) {
      console.error(PREFIX, "Final cart quantity mismatch:", {
        totalSelectedQuantity,

        totalCartQuantity,

        selectedItems,

        finalCartItems,
      });

      throw new Error(
        "The final cart quantity does not match the selected bundle quantity.",
      );
    }

    console.log(PREFIX, "Shopify cart items after filtering:", finalCartItems);

    try {
      if (
        new URLSearchParams(window.location.search).get("view") === "bundle"
      ) {
        sessionStorage.setItem(
          `ftr-loop-bundle-url:${transactionId}`,
          window.location.href,
        );
      }

      sessionStorage.setItem(
        `ftr-loop-bundle-name:${transactionId}`,
        transaction.bundleName,
      );
    } catch (_) {}

    finalCartItems.forEach((item, index) =>
      console.log(PREFIX, `Final item ${index}:`, {
        id: item.id,

        quantity: item.quantity,

        selling_plan: item.selling_plan,

        properties: item.properties,
      }),
    );

    console.log(PREFIX, "Final Loop-style payload:", {
      items: finalCartItems,
    });

    return finalCartItems;
  };

  state.validateAddedBundleLines = async function validateAddedBundleLines(
    finalCartItems,
  ) {
    const loopItems = finalCartItems.filter(
      (item) => item?.properties?._bundleId,
    );

    if (!loopItems.length) {
      return [];
    }

    const txnId = String(loopItems[0].properties._bundleId);

    const response = await fetch(
      `${window.Shopify?.routes?.root || "/"}cart.js`,
      {
        headers: {
          Accept: "application/json",
        },
      },
    );

    const cart = await response.json();

    if (!response.ok) {
      throw new Error("Unable to verify the added bundle.");
    }

    const addedBundleLines = (cart.items || []).filter(
      (item) => String(item?.properties?._bundleId || "") === txnId,
    );

    console.log(PREFIX, "Added bundle lines:", addedBundleLines);

    const expectedQuantity = loopItems.reduce(
      (sum, item) => sum + Number(item.quantity || 0),
      0,
    );

    const actualQuantity = addedBundleLines.reduce(
      (sum, item) => sum + Number(item.quantity || 0),
      0,
    );

    if (actualQuantity !== expectedQuantity) {
      console.error(PREFIX, "Cart quantity mismatch:", {
        expectedQuantity,

        actualQuantity,

        finalCartItems,

        addedBundleLines,
      });

      throw new Error(
        "One or more bundle products were not added to the cart.",
      );
    }

    return addedBundleLines;
  };

  state.createHostedTransaction = createLoopHostedTransaction;

  state.createStorefrontTransaction = createLoopHostedTransaction;

  state.getLoopBundleCartDetails = getLoopBundleCartDetails;

  state.getInternalLoopTransactionData = getInternalLoopTransactionData;

  state.resolveLoopBundleId = () =>
    resolveLoopBundleId(document.querySelector("[data-loop-custom-bundle]"));

  state.initialize = initialize;

  state.refreshPrice = refreshLoopBundlePrice;

  state.updatePurchaseOptionsPriceUI = updatePurchaseOptionsPriceUI;

  state.getSelectedSellingPlanId = getSelectedLoopSellingPlanId;

  state.syncSelectedPlanFromWidget = syncSelectedPlanFromWidget;

  state.isParentBundleLine = isParentBundleLine;

  state.isBundleCartLine = isBundleCartLine;

  state.isLoopTransactionGuid = isLoopTransactionGuid;

  state.removeLoopPropertiesFromOneTimeItem =
    removeLoopPropertiesFromOneTimeItem;

  state.getLoopBundleName = getLoopBundleName;

  state.getLoadedLoopBundleName = getLoadedLoopBundleName;

  state.buildLoopSubscriptionCartItems = buildLoopSubscriptionCartItems;

  state.extractLoopTransactionId = extractLoopTransactionId;

  document.addEventListener("shopify:section:load", (event) =>
    initialize(event.target),
  );

  document.addEventListener("click", (event) => {
    if (
      event.target.closest(
        [
          "[data-preselected-bundle] .select-btn",
          "[data-preselected-bundle] .bundle-item",
        ].join(", "),
      )
    ) {
      requestAnimationFrame(() => refreshLoopBundlePrice());
    }
  });

  document.addEventListener("change", (event) => {
    if (
      event.target.closest(
        ["[data-preselected-bundle]", "[data-loop-box-size-id]"].join(", "),
      )
    ) {
      refreshLoopBundlePrice();
    }
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initialize());
  } else {
    initialize();
  }
})();
